package nl.volleyplanner.web.controller.planner

import kotlinx.browser.document
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.filterNotNull
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.stateIn
import kotlinx.html.*
import kotlinx.html.dom.create
import kotlinx.html.js.div
import nl.volleyplanner.api.auth.AuthLevel
import nl.volleyplanner.api.files.VolunteerImportFileUploadResponse
import nl.volleyplanner.web.CoroutineManager
import nl.volleyplanner.web.base.*
import nl.volleyplanner.web.base.component.bootstrapAlert
import nl.volleyplanner.web.component.FileUploadComponent
import nl.volleyplanner.web.service.WebUserAndOrganisationService
import nl.volleyplanner.web.util.BootstrapColor
import org.w3c.dom.HTMLElement

class VolunteerImportComponent() : HTMLComponent() {
    override fun render(consumer: TagConsumer<HTMLElement>): HTMLElement {
        val activeSeason = WebUserAndOrganisationService.selectedOrganisationState.value?.activeSeason?.value
        val availableSeasons = WebUserAndOrganisationService.selectedOrganisationState.value?.availableSeasons?.value

        return consumer.div {
            h1 (classes = "display-5") { +"Leden importeren" }
            p (classes = "lead") {
                +"De planner kan rekening houden met vrijwilligers door ze minder vaak of helemaal niet in te delen nav de zwaarte van hun functie. "
                unsafe { +"Gebruik van deze functie is <strong>optioneel</strong>. Zonder vrijwilligerstaken behandelt de planner iedereen als gelijkwaardig." }
            }

            div(classes = "row border align-items-stretch mt-5 rounded-top-1") {
                style = "max-width: 850px;"
                div(classes = "col") {
                    div(classes = "row py-2 text-bg-primary bg-gradient d-flex align-items-center") {
                        h4(classes = "col m-0 fw-semibold") { +"Bestand specificaties" }
                    }
                    val firstcolClasses = "col-12 col-sm-3 col-lg-2"
                    val secondcolClasses = "col-12 col-sm-9 col-lg-10"
                    div (classes = "row py-2 align-items-center border-bottom border-secondary-subtle") {
                        div(classes = firstcolClasses) {
                            strong { +"Ondersteunde formaten" }
                        }
                        div(classes = secondcolClasses) {
                            +"xls, xlsx, csv"
                            br {}
                            span (classes = "small text-secondary") {
                                +"Excel: enkel het eerste (meest linker) blad wordt ingelezen"
                            }
                        }
                    }
                    div(classes = "row py-2 bg-secondary-subtle bg-gradient d-flex align-items-center") {
                        h6(classes = "col m-0 fw-semibold") { +"Kolommen" }
                    }
                    div (classes = "row py-2 align-items-center border-bottom border-secondary-subtle") {
                        div(classes = firstcolClasses) {
                            strong { +"Functiegroep" }
                        }
                        div(classes = secondcolClasses) {
                            +"Wordt momenteel niet gebruikt, kan evt. leeg gelaten worden"
                        }
                    }
                    div (classes = "row py-2 align-items-center border-bottom border-secondary-subtle") {
                        div(classes = firstcolClasses) {
                            strong { +"Functie" }
                        }
                        div(classes = secondcolClasses) {
                            +"De naam van de taak"
                            br {}
                            span (classes = "small text-secondary") {
                                +"De planner geeft deze naam terug als feedback als reden waarom iemand niet ingedeeld wordt"
                            }
                        }
                    }
                    div (classes = "row py-2 align-items-center border-bottom border-secondary-subtle") {
                        div(classes = firstcolClasses) {
                            strong { +"Naam" }
                        }
                        div(classes = secondcolClasses) {
                            +"Naam of lidnummer van het lid"
                            br {}
                            span (classes = "small text-secondary") {
                                +"Als je lidnummers gebruikt moet de kolom nog steeds 'Naam' blijven"
                                br {}
                                +"Namen en lidnummers kunnen door elkaar worden gebruikt"
                            }
                        }
                    }
                    div (classes = "row py-2 align-items-center border-bottom border-secondary-subtle") {
                        div(classes = firstcolClasses) {
                            strong { +"Score" }
                        }
                        div(classes = secondcolClasses) {
                            +"De zwaarte van de taak/functie"
                            br {}
                            span (classes = "small text-secondary") {
                                +"Hoe hoger de score, hoe meer de planner het lid zal ontzien"
                                br {}
                                +"Heeft een lid meerdere taken, dan worden de aparte scores bij elkaar opgeteld"
                                br {}
                                unsafe { +"Bij scores van <strong>50 of hoger</strong> worden leden in principe niet meer ingedeeld" }
                            }
                        }
                    }
                    div (classes = "row py-2 align-items-center border-bottom border-secondary-subtle") {
                        div(classes = firstcolClasses) {
                            strong { +"Indelen voor taken" }
                        }
                        div(classes = secondcolClasses) {
                            +"Of de planner het lid automatisch mag indelen of niet"
                            br {}
                            span (classes = "small text-secondary") {
                                +"Mogelijke waardes: "
                                ul {
                                    li {
                                        unsafe { +"<strong>Ja</strong>: Lid wordt ingepland evenredig aan de score. " }
                                        +"In uiterste gevallen ook bij scores >50"
                                    }
                                    li {
                                        unsafe { +"<strong>Nee</strong>: Lid wordt niet automatisch ingepland, ongeacht de score" }
                                        ul {
                                            li {
                                                +"Heeft een lid meerdere taken? Dan is 1 Nee voldoende voor een vrijstelling."
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }

            div (classes = "mt-2 border rounded-2 p-2") {
                h5 (classes = "pb-1 border-bottom") { +"Voorbeeld" }
                table (classes = "table") {
                    thead {
                        tr {
                            th(scope = ThScope.col) { +"Functiegroep" }
                            th(scope = ThScope.col) { +"Functie" }
                            th(scope = ThScope.col) { +"Naam" }
                            th(scope = ThScope.col) { +"Score" }
                            th(scope = ThScope.col) { +"Indelen voor taken" }
                        }
                    }
                    tbody {
                        tr {
                            td { +"Bestuur" }
                            td { +"Voorzitter" }
                            td { +"Lisa de Vries" }
                            td { +"50" }
                            td { +"Nee" }
                        }
                        tr {
                            td { +"Bestuur" }
                            td { +"Penningmeester" }
                            td { +"Jan Bakker" }
                            td { +"50" }
                            td { +"Nee" }
                        }
                        tr {
                            td { +"TC" }
                            td { +"Voorzitter TC" }
                            td { +"Emma Jansen" }
                            td { +"25" }
                            td { +"Ja" }
                        }
                        tr {
                            td { +"TC" }
                            td { +"TC Lid" }
                            td { +"Bram van Dijk" }
                            td { +"15" }
                            td { +"Ja" }
                        }
                        tr {
                            td { +"Trainers" }
                            td { +"Trainer D1" }
                            td { +"Sophie Willems" }
                            td { +"28" }
                            td { +"Ja" }
                        }
                    }
                }
            }

            h4 (classes = "mt-4") {
                +"Upload vrijwilligerstaken"
            }
            bootstrapAlert(BootstrapColor.Warning) {
                classes += "mt-2"

                p {
                    unsafe { +"Voeg altijd een <strong>kopregel</strong> toe met de kolomnamen." }
                    br {}
                    +"Ondersteunde kolomnamen zoals hierboven beschreven: "
                    strong { +"Functiegroep, Functie, Naam, Score, Indelen voor taken" }
                }
                p {
                    unsafe { +"<strong>Let op!</strong> upload altijd een volledig bestand. Alle huidige taken in het systeem worden geupdate/vervangen." }
                }
            }

            if (activeSeason == null) {
                spinner()
            } else {
                if (availableSeasons!!.size > 1) {
                    div (classes = "row py-2 align-items-center") {
                        div(classes = "col-12 col-sm-3") {
                            strong { +"Upload voor seizoen" }
                        }
                        div(classes = "col-12 col-sm-9") {
                            div { strong { +activeSeason.name } }
                            div (classes = "small text-secondary") {
                                +"Vrijwilligerstaken worden enkel voor dit seizoen ingeladen"
                            }
                        }
                    }
                }
                component(
                    FileUploadComponent<VolunteerImportFileUploadResponse>(
                        "volunteers-upload", "volunteers_import", listOf("xls", "xlsx", "csv"),
                        VolunteerImportFileUploadResponse.serializer(),
                        { response ->
                            dl (classes = "row") {
                                dt (classes = "col-3") { +"Totaal vrijwilligerstaken" }
                                dd (classes = "col-9") { +"${response.details!!.volunteersTotal}" }
                                dt (classes = "col-3") { +"Nieuwe taken" }
                                dd (classes = "col-9") { +"${response.details!!.volunteersCreated}" }
                                dt (classes = "col-3") { +"Taken geupdate" }
                                dd (classes = "col-9") { +"${response.details!!.volunteersUpdated}" }
                                dt (classes = "col-3") { +"Taken verwijderd" }
                                dd (classes = "col-9") { +"${response.details!!.volunteersDeleted}" }
                            }
                        },
                        customFields = mapOf(
                            "organisation_season" to activeSeason.name
                        )
                    ))
            }

        }
    }

    @OptIn(ExperimentalCoroutinesApi::class)
    override suspend fun getState() = WebUserAndOrganisationService.selectedOrganisationState
        .filterNotNull()
        .flatMapLatest { it.activeSeason }
        .stateIn(CoroutineManager.RENDER_SCOPE, SharingStarted.Eagerly, WebUserAndOrganisationService.selectedOrganisationState.value?.activeSeason?.value)
}

class VolunteerImportController(override val parent: PlannerIndexController) : StaticPageController() {
    override val name = "VolunteerImportController"
    override val title = "Importeer vrijwilligers"
    override val path = "/importeren/vrijwilligers"
    override val requiredAuthLevel = AuthLevel.AdminUser
    override val childPages = emptyList<WebPageController>()

    override fun createRuntimeInstance() = VolunteerImportController(parent)

    override suspend fun renderContent(): HTMLElement {
        return document.create.div {
            h1 { +"Vrijwilligers importeren" }
            component(VolunteerImportComponent())
        }
    }
}